import { useCallback, useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { styled } from 'styled-components'
import { postGuestOrderSheets, postUserOrderSheets } from '../../api/cart'
import { postNaverOrderSheet } from '../../api/orderHistory'
import { BtnCartPayment } from '../../components/Button'
import { useAppContext } from '../../components/provider/AppProvider'
import { useCommonContext } from '../../components/provider/CommonProvider'
import { AFWebLinkType, handlePostMessage, RNPostMessage } from '../../js/postMessage'
import { getParsedSessionStorageItem, plusComma } from '../../js/script'
import useCartGroup from './useCartGroup'
import { IconArrowDown, IconArrowUp, IconSubBullet } from '../../components/svg/IconSvg'

export default function CartPriceInfo() {
  const navigate: NavigateFunction = useNavigate()

  const { isAppView } = useAppContext()
  const { hasAccessToken } = useCommonContext()

  const { isInvalid, selectOrderVariationMap, selectVariationCartNos, orderProductMap, userCredit, firstPurchase } =
    useCartGroup()

  const [openDiscount, setOpenDiscount] = useState<boolean>(false) //할인 합계보이기

  const categorySpecificProducts = useMemo(() => {
    return Object.values(orderProductMap).filter((product) => {
      return product.categoryNos.includes(787334)
    })
  }, [orderProductMap])

  const calcPaymentPrice = useMemo(() => {
    return Object.values(selectOrderVariationMap).reduce(
      (acc, variation) => {
        return {
          buyAmt: acc.buyAmt + variation.price.buyAmt || 0,
          additionalDiscountAmt:
            acc.additionalDiscountAmt + variation.price.additionalDiscountAmt * variation.orderCnt || 0,
          immediateDiscountAmt:
            acc.immediateDiscountAmt + variation.price.immediateDiscountAmt * variation.orderCnt || 0,
          standardAmt: acc.standardAmt + variation.price.standardAmt || 0,
          salePrice: acc.salePrice + variation.price.salePrice || 0,
          addPrice: acc.addPrice + variation.price.addPrice || 0,
        }
      },
      {
        buyAmt: 0,
        additionalDiscountAmt: 0,
        immediateDiscountAmt: 0,
        standardAmt: 0,
        salePrice: 0,
        addPrice: 0,
      }
    )
  }, [selectOrderVariationMap])

  const isInValidSelectVariation = useMemo(() => {
    return Object.values(selectOrderVariationMap).some((variation) => {
      return variation.validInfo?.valid === false
    })
  }, [selectOrderVariationMap]) // 선택된 장바구니 상품이 유효한지 체크

  const onClickDiscount = () => {
    setOpenDiscount((prev) => !prev)
  }

  const onClickGuestNaverPay = useCallback(() => {
    if (Object.values(selectOrderVariationMap).length === 0) {
      return alert('구매할 상품이 없습니다.')
    }
    if (isInValidSelectVariation) {
      return alert('구매 불가한 상품이 있습니다.')
    }

    if (window.confirm('npay로 구매하면 네이버에서만 주문내역을 확인할 수 있습니다.')) {
      const app = getParsedSessionStorageItem('app')
      const guestVariationList = Object.values(selectOrderVariationMap).map((variation) => {
        return {
          cartNo: null,
          productNo: variation.productNo,
          optionNo: variation.optionNo,
          orderCnt: variation.orderCnt,
          channelType: null,
        }
      })

      if (app) {
        postNaverOrderSheet(guestVariationList, true).then((res) => {
          handlePostMessage({
            actionType: 'moveToLinkNaver',
            params: { url: res.data },
          })
        })
      } else {
        const open = window.open('about:blank', '_blank')
        postNaverOrderSheet(guestVariationList, false).then((res) => {
          //@ts-ignore
          open.location.href = res.data
        })
      }
    }
  }, [selectOrderVariationMap, isInValidSelectVariation])

  const onClickCartPayment = useCallback(async () => {
    if (Object.values(selectOrderVariationMap).length === 0) {
      return alert('구매할 상품이 없습니다.')
    }
    if (isInValidSelectVariation) {
      return alert('구매 불가한 상품이 있습니다.')
    }

    try {
      if (!hasAccessToken) {
        // NOTE: 비회원 결제
        const guestVariationList = Object.values(selectOrderVariationMap).map((variation) => {
          return {
            cartNo: null,
            productNo: variation.productNo,
            optionNo: variation.optionNo,
            orderCnt: variation.orderCnt,
            channelType: null,
          }
        })
        const { data } = await postGuestOrderSheets(selectVariationCartNos, guestVariationList)

        if (isAppView) {
          RNPostMessage(AFWebLinkType.moveToLink, {
            url: `aboutfishing://aboutfishing.com/?page=webview&url=${process.env.REACT_APP_SHOP_URL}/order/${data.orderSheetNo}`,
          })
        } else {
          navigate(`/order/${data.orderSheetNo}`)
        }
      } else {
        // 100원샵 상품 조건 처리
        if (categorySpecificProducts.length > 1) {
          return alert('100원샵 상품은 하나만 구매 가능합니다.')
        }

        if (categorySpecificProducts.length >= 1 && firstPurchase) {
          return alert('100원샵 상품 이용가능 고객이 아닙니다. 장바구니에서 제외 후 구매해주세요.')
        }

        // 최소 금액 조건 처리
        if (categorySpecificProducts.length >= 1 && calcPaymentPrice?.buyAmt < 30000) {
          return alert('100원 상품과 함께 담은 상품의 금액이 3만원 이상이어야 합니다.')
        }

        // NOTE: 회원 결제
        const variationList = Object.values(selectOrderVariationMap).map((variation) => {
          return {
            cartNo: variation.cartNo,
            productNo: variation.productNo,
            optionNo: variation.optionNo,
            orderCnt: variation.orderCnt,
            channelType: null,
          }
        })

        const { data } = await postUserOrderSheets(selectVariationCartNos, variationList)

        if (isAppView) {
          RNPostMessage(AFWebLinkType.moveToLink, {
            url: `aboutfishing://aboutfishing.com/?page=webview&url=${process.env.REACT_APP_SHOP_URL}/order/${data.orderSheetNo}`,
          })
        } else {
          navigate(`/order/${data.orderSheetNo}`)
        }

        handlePostMessage({
          actionType: 'goOrder',
          params: { mobile: isMobile ? 'mobile' : 'pc' },
        })
      }
    } catch (error) {
      console.error('장바구니 결제 오류', error)
      alert('구매 불가한 상품이 있습니다.')
    }
  }, [
    hasAccessToken,
    isInValidSelectVariation,
    selectVariationCartNos,
    selectOrderVariationMap,
    categorySpecificProducts,
    calcPaymentPrice,
    firstPurchase,
  ])

  return (
    <div className='right_area'>
      <div className='right_area_info'>
        <CartListSumPrice>
          <dl>
            <dt>전체 상품금액</dt>
            <dd>{isInvalid ? 0 : plusComma(calcPaymentPrice.standardAmt)}원</dd>
          </dl>

          {hasAccessToken &&
            !isInvalid &&
            calcPaymentPrice.buyAmt > calcPaymentPrice.immediateDiscountAmt + calcPaymentPrice.additionalDiscountAmt &&
            calcPaymentPrice.immediateDiscountAmt + calcPaymentPrice.additionalDiscountAmt > 0 && (
              <MaximumExpectedDiscount>
                <li className={openDiscount ? 'on' : ''} onClick={onClickDiscount}>
                  <dl>
                    <dt>
                      최대예상 할인금액 합계
                      {openDiscount ? (
                        <IconArrowUp width={16} height={16} color='var(--colPointOrange)' />
                      ) : (
                        <IconArrowDown width={16} height={16} color='var(--colPointOrange)' />
                      )}
                    </dt>
                    <dd>
                      - {plusComma(calcPaymentPrice.immediateDiscountAmt + calcPaymentPrice.additionalDiscountAmt)}원
                    </dd>
                  </dl>
                  <ul>
                    <li>
                      {calcPaymentPrice.immediateDiscountAmt > 0 && (
                        <dl>
                          <dt>
                            <IconSubBullet />
                            즉시 할인금액
                          </dt>
                          <dd>- {plusComma(calcPaymentPrice.immediateDiscountAmt)}원</dd>
                        </dl>
                      )}
                    </li>
                    <li>
                      {calcPaymentPrice.additionalDiscountAmt > 0 && (
                        <dl>
                          <dt>
                            <IconSubBullet />
                            추가 할인금액
                          </dt>
                          <dd>- {plusComma(calcPaymentPrice.additionalDiscountAmt)}원</dd>
                        </dl>
                      )}
                    </li>
                  </ul>
                </li>
              </MaximumExpectedDiscount>
            )}

          <dl className='total_payment_amount'>
            <dt>총 결제 금액</dt>
            <dd>{isInvalid ? 0 : plusComma(calcPaymentPrice.buyAmt)}원</dd>
          </dl>

          {hasAccessToken &&
            !isInvalid &&
            calcPaymentPrice.buyAmt > userCredit.totalDiscountAmt &&
            calcPaymentPrice.buyAmt - userCredit.totalDiscountAmt > 0 && (
              <dl className='coupon_discount_amount'>
                <dt>쿠폰 적용 시</dt>
                <dd>
                  {/* <span>{plusComma(calcPaymentPrice.buyAmt)}원</span> */}
                  {userCredit.totalDiscountAmt > 0 && <span>- {plusComma(userCredit.totalDiscountAmt)}원</span>}
                  <span>
                    <strong>{plusComma(calcPaymentPrice.buyAmt - userCredit.totalDiscountAmt)}</strong>원
                  </span>
                </dd>
              </dl>
            )}

          <p className='summary'>쿠폰 적용 시 금액은 주문서와 다를 수 있습니다.</p>
          <p className='summary'>배송비·적립금을 포함한 최종 금액은 주문서에서 확인할 수 있습니다.</p>
        </CartListSumPrice>

        {/* 네이버 페이 영역 */}
        {!hasAccessToken && (
          <NaverPay disabled={isInvalid} onClick={onClickGuestNaverPay}>
            <img src={`/images/icon_npay_logo.png`} alt='네이버페이 이미지' />
            구매하기
          </NaverPay>
        )}

        <CartTotalPayment className='btn_fixed'>
          <CartTotalPaymentButton disabled={isInvalid} onClick={onClickCartPayment}>
            {isInvalid ? 0 : plusComma(calcPaymentPrice.buyAmt)}원 구매하기
          </CartTotalPaymentButton>
        </CartTotalPayment>
      </div>
    </div>
  )
}

// 장바구니 상품금액 합계, 할인 금액 합계, 배송비
export const CartListSumPrice = styled.div`
  padding: 24px 0;
  margin: 0 20px 24px 20px;
  border-bottom: 1px var(--colBlack) solid;
  & > dl {
    width: 100%;
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    align-items: flex-end;
    & > dt {
      font-size: 14px;
      font-weight: 400;
      line-height: 18px;
    }
    & > dd {
      font-size: 14px;
      font-weight: 500;
      line-height: 16px;
    }
  }
  & dl.coupon_discount_amount {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--colGray2);
    & > dt,
    & > dd {
      font-weight: 500;
      color: var(--colPointBlue);
      * {
        font-weight: 500;
        color: var(--colPointBlue);
      }
    }
    & > dd {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 4px;
    }
  }
  /* 총 결제금액 */
  & dl.total_payment_amount {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--colGray2);
    & > dt {
      font-size: 16px;
      font-weight: 700;
      line-height: 24px;
    }
    & > dd {
      font-size: 20px;
      font-weight: 700;
      line-height: 24px;
    }
  }
  /* 예상 적립 마일리지 */
  & dl.mileage {
    display: flex;
    justify-content: flex-end;
    gap: 4px;
    font-size: 12px;
    font-weight: 500;
    line-height: 14px;
    color: var(--colPointBlue);
  }
  @media screen and (min-width: 768px) {
    padding: 0 0 24px 0;
    margin: 0 0 24px 0;
  }
  .summary {
    display: flex;
    margin-top: 4px;
    gap: 2px;
    align-items: center;
    justify-content: flex-end;
    font-size: 12px;
    color: var(--colGray6);
    &:before {
      content: '※';
    }
  }
`
// 최대 예상 할인 금액 합계
export const MaximumExpectedDiscount = styled.ul`
  & > li {
    overflow: hidden;
    & > dl {
      color: var(--colPointOrange);
      & dt {
        display: flex;
        gap: 4px;
        justify-content: center;
        align-items: center;
        font-weight: 400;
      }
      & dd {
        font-weight: 500;
      }
    }
    & > ul {
      display: none;
      & > li dl dt svg {
        margin-right: 4px;
      }
      & > li:last-child {
        margin-bottom: none;
      }
    }
  }
  & dl {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    color: var(--colGray5);
    margin-bottom: 8px;
  }
  & > li.on {
    & > ul {
      display: block;
    }
  }
`
export const CartOrderButton = styled.button`
  outline: none;
  border: none;
  background: none;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`
// 네이버 페이 영역 (호출하는 것인지 확인 필요)
export const NaverPay = styled(CartOrderButton)`
  display: flex;
  width: calc(100% - 40px);
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 4px;
  height: 40px;
  background: #00de5a;
  box-shadow: inset 0 0 0 1px #00c851;
  border-radius: 5px;
  margin: 0 20px;
  color: var(--colBlack);
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
  cursor: pointer;
  & img {
    height: 16px;
  }
  @media screen and (min-width: 768px) {
    gap: 5px;
    width: 100%;
    height: 48px;
    margin: 0;
    color: var(--colBlack);
    font-size: 15px;
    font-weight: 600;
    line-height: 18px;
    cursor: pointer;
    & img {
      height: 18px;
    }
  }
`
// 장바구니 결제버튼 영역
export const CartTotalPayment = styled.div`
  width: 100%;
  position: fixed;
  left: 0;
  bottom: 0;
  padding: 12px 20px;
  z-index: 1;
  background: var(--colWhite);
  @media screen and (min-width: 768px) {
    position: static;
    padding: 20px 0 0 0;
  }
`
export const CartTotalPaymentButton = styled(BtnCartPayment)`
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`
